import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import { useGetDailyMaterialListQuery } from "../../../../store/appApi";
import { selectHasModuleAccess } from "../../../../store/authSlice";

const ListDailyMaterial = () => {
  const navigate = useNavigate();
  const hasRead = useSelector(selectHasModuleAccess("workProgress", "read"));
  const hasWrite = useSelector(
    selectHasModuleAccess("workProgress", "read-write"),
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetDailyMaterialListQuery(undefined, { skip: !hasRead });

  const reports = data?.reports ?? [];
  const loading = isLoading || isFetching;

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—";

  if (!hasRead) {
    return (
      <>
        <PageTitle
          activeMenu="Daily Progress Reports"
          motherMenu="Daily Progress"
        />
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
                <h5>Access Restricted</h5>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu="Daily Progress Reports"
        motherMenu="Daily Progress"
      />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h4 className="card-title mb-0">
                Daily Material Progress Reports
              </h4>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={refetch}
                  disabled={loading}
                  title="Refresh"
                >
                  <i className={`fa fa-refresh ${loading ? "fa-spin" : ""}`} />
                </button>
                {hasWrite && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => navigate("/progress/daily-material/add")}
                  >
                    + Add Daily Report
                  </button>
                )}
              </div>
            </div>

            <div className="card-body">
              {isError && (
                <div className="alert alert-danger">
                  Failed to load reports.{" "}
                  <button
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={refetch}
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className="table-responsive">
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Materials</th>
                      <th>Added By</th>
                      <th>Linked Morning Report</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!loading && reports.length === 0 && !isError && (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          No daily progress reports yet.{" "}
                          {hasWrite && (
                            <button
                              className="btn btn-sm btn-link p-0"
                              onClick={() =>
                                navigate("/progress/daily-material/add")
                              }
                            >
                              Add the first one
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      reports.map((r, idx) => (
                        <tr key={r._id}>
                          <td className="text-muted small">{idx + 1}</td>
                          <td className="fw-semibold">{fmtDate(r.date)}</td>
                          <td>
                            <span className="badge badge-primary light">
                              {r.materialCount} rows
                            </span>
                          </td>
                          <td className="text-muted small">
                            {r.reportedByName}
                          </td>
                          <td>
                            {r.morningReportId ? (
                              <span className="badge badge-success light">
                                Linked
                              </span>
                            ) : (
                              <span className="badge badge-secondary light">
                                Manual
                              </span>
                            )}
                          </td>
                          <td className="d-flex gap-1">
                            <button
                              className="btn btn-xs btn-outline-primary"
                              onClick={() =>
                                navigate(
                                  `/progress/daily-material/detail/${r._id}`,
                                )
                              }
                            >
                              <i className="fa fa-eye me-1" />
                              View
                            </button>
                            {hasWrite && (
                              <button
                                className="btn btn-xs btn-outline-warning"
                                onClick={() =>
                                  navigate(
                                    `/progress/daily-material/edit/${r._id}`,
                                  )
                                }
                              >
                                <i className="fa fa-pencil me-1" />
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListDailyMaterial;
